// context/AuthContext.jsx
import { createContext, useContext, useState, useEffect } from "react";
import { login as apiLogin, register as apiRegister, loginWithIdToken as apiLoginWithIdToken, getProfile, refreshAccessToken } from "../services/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem("token"));
  const [refreshToken, setRefreshToken] = useState(() => localStorage.getItem("refreshToken"));
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem("user");
      // if localStorage contains "undefined" or empty, skip parsing
      return raw && raw !== "undefined" ? JSON.parse(raw) : null;
    } catch {
      // fallback if JSON.parse fails
      return null;
    }
  });
  
  // Start with isRefreshing=true if we have a refresh token (token may be expired or missing after restart)
  // This prevents ProtectedRoute from redirecting to /login before we attempt a refresh
  const [isRefreshing, setIsRefreshing] = useState(() => {
    const hasRefreshToken = localStorage.getItem("refreshToken");
    return !!hasRefreshToken;
  });

  // Refresh token on app startup if needed
  useEffect(() => {
    // Get fresh values from localStorage directly
    const storedToken = localStorage.getItem("token");
    const storedRefreshToken = localStorage.getItem("refreshToken");
    
    // Debug logging in development only
    if (process.env.NODE_ENV === 'development') {
      console.log("🔄 Auth startup check:", {
        hasToken: !!token,
        hasRefreshToken: !!refreshToken,
        storedToken: !!storedToken,
        storedRefreshToken: !!storedRefreshToken
      });
    }
    
    // If no refresh token in localStorage, nothing to refresh
    if (!storedRefreshToken) {
      setIsRefreshing(false);
      return;
    }

    // Try to refresh immediately on app load (in case token expired while PWA was closed)
    const refreshOnStartup = async () => {
      console.log("🔄 Starting automatic token refresh on app startup...");
      console.log("   Using refreshToken:", storedRefreshToken.substring(0, 30) + "...");
      
      try {
        const newToken = await refreshAccessToken(storedRefreshToken);
        if (newToken) {
          console.log("✅ Got new access token:", newToken.substring(0, 30) + "...");
          setToken(newToken);
          localStorage.setItem("token", newToken);
          console.log("✅ Access token refreshed successfully - User auto-logged in");
          
          // Also refresh user profile data
          try {
            const response = await getProfile();
            console.log("📦 Profile response:", response);
            if (response.ok && response.data) {
              setUser(response.data);
              localStorage.setItem("user", JSON.stringify(response.data));
              console.log("✅ User profile refreshed:", response.data.email);
            } else {
              // Profile failed to load - token might be invalid or database issue
              console.error("❌ Failed to load user profile - forcing logout");
              localStorage.removeItem("token");
              localStorage.removeItem("refreshToken");
              localStorage.removeItem("user");
              setToken(null);
              setRefreshToken(null);
              setUser(null);
            }
          } catch (profileError) {
            console.error("❌ Could not refresh user profile:", profileError);
            // If profile fetch fails, clear auth to force re-login
            localStorage.removeItem("token");
            localStorage.removeItem("refreshToken");
            localStorage.removeItem("user");
            setToken(null);
            setRefreshToken(null);
            setUser(null);
          }
        } else {
          // Refresh token might be expired (after 90 days)
          console.warn("⚠️ Refresh token expired or invalid - will logout on next protected route access");
          // Don't force logout immediately - let ProtectedRoute handle it
          // This prevents unnecessary logouts when navigating public pages
        }
      } catch (error) {
        console.error("❌ Startup token refresh failed:", error);
        // Don't force logout on network errors or backend sleeping
        // User can still access public pages, protected routes will redirect to login
      } finally {
        console.log("✅ Refresh complete, setting isRefreshing=false");
        setIsRefreshing(false);
      }
    };

    refreshOnStartup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // Auto-refresh token before expiry (every 10 minutes)
  useEffect(() => {
    if (!refreshToken || !token) return;

    console.log("🔄 Setting up automatic token refresh interval (every 10 minutes)");
    
    const interval = setInterval(async () => {
      console.log("🔄 Auto-refresh triggered (scheduled refresh)");
      try {
        const newToken = await refreshAccessToken(refreshToken);
        if (newToken) {
          setToken(newToken);
          localStorage.setItem("token", newToken);
          console.log("✅ Access token refreshed automatically (background refresh)");
        } else {
          console.warn("⚠️ Auto-refresh failed - refresh token may be expired");
          // Don't logout immediately - might be temporary network issue
          // User will be logged out when trying to access protected routes
        }
      } catch (error) {
        console.error("❌ Auto token refresh failed:", error);
        // Don't logout on network errors - backend might be waking up
        // User can still browse public pages
      }
    }, 10 * 60 * 1000); // Every 10 minutes (token expires in 15 min, so this provides 5 min buffer)

    return () => {
      console.log("🛑 Clearing auto-refresh interval");
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken, token]);

  // DISABLED: Keep-alive mechanism removed to reduce hosting costs
  // This was pinging backend every 4 minutes to keep Render backend awake
  // For cost-effective hosting, we let the backend sleep when not in use
  // Backend will auto-wake on user requests (may add 1-2 second delay on first load)
  
  // If you're using a hosting platform that charges for uptime (like Railway),
  // leave this DISABLED. If you need instant response times and have
  // a hosting plan that doesn't charge extra for always-on, you can re-enable this.
  
  /* COMMENTED OUT TO SAVE COSTS:
  useEffect(() => {
    const keepAliveInterval = setInterval(async () => {
      try {
        const base = (process.env.REACT_APP_API_BASE || "http://localhost:4000").replace(/\/$/, '');
        await fetch(`${base}/health`, { method: 'GET' });
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn("⚠️ Keep-alive ping failed:", error);
        }
      }
    }, 4 * 60 * 1000);
    return () => clearInterval(keepAliveInterval);
  }, []);
  */

  function saveAuth(t, rt, u) {
    console.log("💾 saveAuth called with:", {
      hasToken: !!t,
      hasRefreshToken: !!rt,
      hasUser: !!u,
      tokenPreview: t ? t.substring(0, 30) + "..." : "NULL",
      refreshTokenPreview: rt ? rt.substring(0, 30) + "..." : "NULL",
      userEmail: u?.email || "NULL"
    });
    
    // Check token expiry before saving
    if (t) {
      try {
        const parts = t.split('.');
        const payload = JSON.parse(atob(parts[1]));
        const expiresIn = payload.exp - Math.floor(Date.now() / 1000);
        console.log("🕐 Token expiry:", {
          expiresAt: new Date(payload.exp * 1000).toISOString(),
          expiresInSeconds: expiresIn,
          expiresInMinutes: Math.floor(expiresIn / 60)
        });
      } catch (e) {
        console.error("❌ Failed to decode token:", e);
      }
    }
    
    setToken(t);
    setRefreshToken(rt);
    setUser(u);
    
    if (t) {
      localStorage.setItem("token", t);
      localStorage.setItem("wasLoggedIn", "true"); // Mark that user was logged in
    }
    if (rt) localStorage.setItem("refreshToken", rt);
    if (u) localStorage.setItem("user", JSON.stringify(u));
    
    console.log("✅ Tokens saved to state and localStorage");
    console.log("   localStorage.token:", localStorage.getItem("token") ? "SAVED" : "MISSING");
    console.log("   localStorage.refreshToken:", localStorage.getItem("refreshToken") ? "SAVED" : "MISSING");
    console.log("   localStorage.user:", localStorage.getItem("user") ? "SAVED" : "MISSING");
  }

  async function refreshUser() {
    if (!token) return;
    
    try {
      const response = await getProfile();
      if (response.ok && response.data) {
        setUser(response.data);
        localStorage.setItem("user", JSON.stringify(response.data));
      }
    } catch (error) {
      console.error("Failed to refresh user data:", error);
    }
  }

  async function login(email, password, rememberMe = false) {
    console.log("🔐 Login function called");
    console.log("   Email:", email);
    console.log("   RememberMe:", rememberMe);
    
    const res = await apiLogin(email, password, rememberMe);
    console.log("📦 Login API response:", res);
    console.log("   Response has .data?", !!res.data);
    console.log("   Response has .token?", !!res.token);
    console.log("   Response.data structure:", res.data ? Object.keys(res.data) : "N/A");
    
    // Be resilient to backend responses that only return { token } without refreshToken/user
    const accessToken = (res?.data?.token) || res?.token;
    const refToken = (res?.data?.refreshToken) || res?.refreshToken || null;
    let userData = (res?.data?.user) || res?.user || null;

    console.log("📤 Extracted from response:");
    console.log("   accessToken:", accessToken ? accessToken.substring(0, 30) + "..." : "UNDEFINED");
    console.log("   refToken:", refToken ? refToken.substring(0, 30) + "..." : "UNDEFINED");
    console.log("   userData:", userData ? userData.email : "UNDEFINED");

    if (!accessToken) {
      console.error("❌ Login failed: access token missing");
      console.error("   Full response:", JSON.stringify(res, null, 2));
      throw new Error("Login response missing token");
    }

    // Save what we have immediately so subsequent calls (e.g., getProfile) include auth header
    saveAuth(accessToken, refToken, userData);

    // If user data is missing, fetch it now
    if (!userData) {
      try {
        console.warn("⚠️ User absent in login response; fetching profile...");
        const profileRes = await getProfile();
        if (profileRes?.ok && profileRes?.data) {
          userData = profileRes.data;
          setUser(userData);
          localStorage.setItem("user", JSON.stringify(userData));
          console.log("✅ User profile fetched after login:", userData.email);
        } else {
          console.warn("⚠️ Could not fetch profile after login. Response:", profileRes);
        }
      } catch (e) {
        console.warn("⚠️ getProfile failed after login:", e);
      }
    }

    // If refresh token is missing, warn but don't block login
    if (!refToken) {
      console.warn("⚠️ No refreshToken in login response. 'Remember me' persistence across restarts may not work until backend returns refreshToken.");
    }

    console.log("✅ Login complete - token saved; user:", userData?.email || "(unknown)");
  }

  async function loginWithIdToken(idToken, rememberMe = false) {
    const res = await apiLoginWithIdToken(idToken, rememberMe);
    const accessToken = (res?.data?.token) || res?.token;
    const refToken = (res?.data?.refreshToken) || res?.refreshToken || null;
    let userData = (res?.data?.user) || res?.user || null;

    if (!accessToken) throw new Error("OAuth response missing token");
    saveAuth(accessToken, refToken, userData);

    if (!userData) {
      try {
        const profileRes = await getProfile();
        if (profileRes?.ok && profileRes?.data) {
          userData = profileRes.data;
          setUser(userData);
          localStorage.setItem("user", JSON.stringify(userData));
        }
      } catch (e) {
        console.warn("getProfile after OAuth failed", e);
      }
    }
  }

  async function register(email, password, rememberMe = false) {
    const res = await apiRegister(email, password, rememberMe);
    const { token: accessToken, refreshToken: refToken, user: userData } = res.data || res;
    saveAuth(accessToken, refToken, userData);
  }

  function logout() {
    setToken(null);
    setRefreshToken(null);
    setUser(null);
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
  }

  return (
    <AuthContext.Provider value={{ token, refreshToken, user, login, loginWithIdToken, register, logout, refreshUser, isRefreshing }}>
      {children}
    </AuthContext.Provider>
  );
  
}

export function useAuth() {
  return useContext(AuthContext);
}

